import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { usePortalAuthStore } from '@/stores/portalAuthStore';
import { usePortalBranding } from '@/hooks/usePortalBranding';

/**
 * The white-labeled client-portal shell (stories 128.2 + 128.4). Applies the
 * tenant's branding (logo + colors via usePortalBranding) and frames the
 * read-only client views. Deliberately minimal chrome — no staff sidebar, no
 * module nav: a client sees only its own proof-of-performance.
 */
export function PortalLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clear = usePortalAuthStore((s) => s.clear);
  const { branding } = usePortalBranding();

  function logout() {
    clear();
    navigate('/portal/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-[var(--color-lop-bg-deep)] text-[var(--color-lop-text-primary)]">
      <header className="flex items-center justify-between border-b border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] px-6 py-3">
        <div className="flex items-center gap-3">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={t('portal.shell.logoAlt')}
              className="h-8 w-auto max-w-[180px] object-contain"
            />
          ) : (
            <span className="text-base font-semibold tracking-tight">
              {t('portal.shell.fallbackTitle')}
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={logout}>
          {t('portal.shell.logout')}
        </Button>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
